import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { BRANCHES as STATIC_BRANCHES } from "../lib/data";
import { useSettings } from "../lib/settings";
import { ExternalLink } from "lucide-react";
import { formatIndianPhone, telHref } from "../lib/phone";
import { trackEvent } from "../lib/analytics";
import Reveal from "./Reveal";

const pinIcon = L.divIcon({
  className: "bst-pin",
  html: `
    <div style="position:relative;width:34px;height:44px;">
      <svg width="34" height="44" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="s" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,.35)"/>
          </filter>
        </defs>
        <path filter="url(#s)" d="M17 0C7.6 0 0 7.6 0 17c0 12 17 27 17 27s17-15 17-27c0-9.4-7.6-17-17-17z" fill="#e85a1c"/>
        <circle cx="17" cy="17" r="6" fill="#fff"/>
      </svg>
    </div>
  `,
  iconSize: [34, 44],
  iconAnchor: [17, 44],
  popupAnchor: [0, -40],
});

// Recenters/zooms whenever branches list changes
const Recenter = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [center, zoom, map]);
  return null;
};

/**
 * LocationsMap
 *  - normal mode: shows 4 pins with popups
 *  - admin mode (props.adminMode=true, onMove callback): pins are draggable
 *    and emit new coords via onMove(name, lat, lng)
 */
export const LocationsMap = ({ adminMode = false, branchesOverride, onMove }) => {
  const { branches: ctxBranches } = useSettings();
  // Merge static metadata (address/phone/email) with provided coords (override from admin form, else server)
  const branches = useMemo(() => {
    if (branchesOverride && branchesOverride.length) {
      return STATIC_BRANCHES.map((b) => {
        const o = branchesOverride.find((x) => x.name === b.name);
        return o ? { ...b, coords: [o.lat, o.lng] } : b;
      });
    }
    return ctxBranches;
  }, [branchesOverride, ctxBranches]);

  const center = useMemo(() => {
    const lats = branches.map((b) => b.coords[0]);
    const lngs = branches.map((b) => b.coords[1]);
    return [
      (Math.min(...lats) + Math.max(...lats)) / 2,
      (Math.min(...lngs) + Math.max(...lngs)) / 2,
    ];
  }, [branches]);

  // Body when used in admin (no surrounding section)
  if (adminMode) {
    return (
      <div
        data-testid="locations-map-admin"
        className="overflow-hidden rounded-2xl border border-black/5 shadow-lux"
      >
        <MapContainer
          center={center}
          zoom={11}
          scrollWheelZoom
          style={{ height: "440px", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap, &copy; CARTO'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          <Recenter center={center} zoom={11} />
          {branches.map((b, i) => (
            <DraggableMarker key={b.name} branch={b} index={i} onMove={onMove} />
          ))}
        </MapContainer>
      </div>
    );
  }

  return (
    <section
      data-testid="locations-section"
      className="border-b border-black/5 bg-white py-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / 03 Our Locations
            </div>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.92] tracking-tighter text-[#1c1c1e] md:text-6xl">
              Four yards.
              <br />
              One <span className="text-[#e85a1c]">city.</span>
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-[#4a4a4e]">
            Strategically positioned across Hyderabad &amp; Secunderabad so
            same-day dispatch reaches your site, no matter which side of
            the twin cities you build on.
          </p>
        </Reveal>

        <Reveal className="grid gap-6 md:grid-cols-12">
          <div
            data-testid="locations-map-container"
            className="overflow-hidden rounded-2xl border border-black/5 shadow-lux md:col-span-8"
          >
            <MapContainer
              center={center}
              zoom={11}
              scrollWheelZoom={false}
              style={{ width: "100%" }}
              className="z-0 h-[360px] md:h-[520px]"
            >
              <TileLayer
                attribution='&copy; OpenStreetMap, &copy; CARTO'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              />
              <Recenter center={center} zoom={11} />
              {branches.map((b, i) => (
                <Marker
                  key={b.name}
                  position={b.coords}
                  icon={pinIcon}
                  data-testid={`map-pin-${i}`}
                >
                  <Popup>
                    <div className="min-w-[220px]">
                      <div className="font-display-med text-[13px] uppercase tracking-tight text-[#1c1c1e]">
                        {b.name}
                      </div>
                      <div className="mt-2 text-xs leading-relaxed text-[#4a4a4e]">
                        {b.address}
                      </div>
                      <div className="mt-2 space-y-1 text-xs">
                        <a
                          href={telHref(b.phone)}
                          onClick={() => trackEvent("phone_click", { source: "map_popup", branch: b.name, phone_number: b.phone })}
                          className="block font-mono-industrial text-[#e85a1c]"
                        >
                          {formatIndianPhone(b.phone)}
                        </a>
                        <a href={`mailto:${b.email}`} className="block break-all text-[#4a4a4e]">
                          {b.email}
                        </a>
                      </div>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${b.coords[0]},${b.coords[1]}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#e85a1c] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white"
                      >
                        Open in Maps <ExternalLink size={10} />
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <div className="md:col-span-4 space-y-3">
            {branches.map((b, i) => (
              <a
                key={b.name}
                href={`https://www.google.com/maps/search/?api=1&query=${b.coords[0]},${b.coords[1]}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`location-card-${i}`}
                className="group block rounded-xl border border-black/5 bg-[#f7f7f5] p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#e85a1c]/40 hover:bg-white hover:shadow-lux"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#e85a1c] text-[10px] font-bold text-white">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="font-display-med text-[12px] uppercase leading-snug tracking-tight text-[#1c1c1e]">
                      {b.name}
                    </div>
                    <div className="mt-1 text-[11px] leading-relaxed text-[#4a4a4e]">
                      {b.address}
                    </div>
                    <div className="mt-1.5 font-mono-industrial text-[11px] text-[#e85a1c]">
                      {formatIndianPhone(b.phone)}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const DraggableMarker = ({ branch, index, onMove }) => {
  const ref = useRef(null);
  return (
    <Marker
      draggable
      position={branch.coords}
      icon={pinIcon}
      ref={ref}
      eventHandlers={{
        dragend: (e) => {
          const ll = e.target.getLatLng();
          onMove?.(branch.name, ll.lat, ll.lng);
        },
      }}
      data-testid={`admin-map-pin-${index}`}
    >
      <Popup>
        <div className="min-w-[180px]">
          <div className="font-display-med text-[12px] uppercase tracking-tight text-[#1c1c1e]">
            {branch.name}
          </div>
          <div className="mt-1 font-mono-industrial text-[10px] text-[#8b8b8f]">
            {branch.coords[0].toFixed(5)}, {branch.coords[1].toFixed(5)}
          </div>
          <div className="mt-2 text-[10px] text-[#4a4a4e]">
            Drag the pin to relocate.
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

export default LocationsMap;
